import { useState, useEffect } from "react";
import { apiGet } from "../lib/apiClient.js";
import { makeTtlCache } from "../lib/forecast/reportCache.js";
import { fetchAlternateResolution } from "../lib/forecast/alternateSpaces.js";

// Module singleton, so reopening the report or scrubbing back to a fire is a hit.
// A final answer never changes; the TTL only bounds how long a cached "not yet" (204) holds.
const ALTERNATE_RESOLUTION_TTL_MS = 5 * 60 * 1000;
const _alternateResolutionCache = makeTtlCache({ ttlMs: ALTERNATE_RESOLUTION_TTL_MS });

/**
 * Keyed by the report's own row key, so a scrubbed older fire shows its own answer and
 * a resolve for a superseded key never lands on the current one. A cache hit is
 * answered synchronously on the first render, so reopening a report does not flash.
 *
 * The read's AbortController is armed BEFORE the await and aborted on cleanup, so a
 * stale in-flight read can neither set state nor populate the cache.
 *
 * @param {{
 *   reportRowKey?: string|null,
 *   enabled?: boolean,          // flag on, alternate on screen, contract resolution settled
 *   apiGetImpl?: Function,
 *   cache?: object,
 * }} args
 * @returns {ReturnType<typeof import("../lib/forecast/alternateSpaces.js").extractAlternateResolution>}
 */
export function useAlternateResolution({
  reportRowKey,
  enabled,
  apiGetImpl = apiGet,
  cache = _alternateResolutionCache,
}) {
  const key = enabled && reportRowKey ? reportRowKey : null;
  const [state, setState] = useState({ key: null, value: null });
  useEffect(() => {
    if (!key) return undefined;
    if (cache.get(key) !== undefined) return undefined;
    const ac = new AbortController();
    fetchAlternateResolution(key, { apiGet: apiGetImpl, cache, signal: ac.signal })
      .then((value) => {
        if (!ac.signal.aborted) setState({ key, value });
      })
      .catch(() => {
        if (!ac.signal.aborted) setState({ key, value: null });
      });
    return () => ac.abort();
    // apiGetImpl / cache are stable singletons (or test-injected); the key is the only
    // real input.
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!key) return null;
  if (state.key === key) return state.value;
  const cached = cache.get(key);
  return cached === undefined ? null : cached;
}
